import { useEffect, useRef } from 'react';
import { Socket } from 'socket.io-client';

/**
 * Manages server-side room subscriptions/unsubscriptions
 * Automatically subscribes/unsubscribes when subscriptions array changes
 */
export function useSocketSubscriptions(socket: Socket | null, socketReady: boolean, subscriptions: string[]) {
  // Track previous subscriptions to detect changes and unsubscribe from removed rooms
  const previousSubscriptionsRef = useRef<string[]>([]);

  // Subscription management effect
  // Handles server-side room subscriptions/unsubscriptions when subscriptions array changes
  // This is separate from event listeners - subscriptions tell the server which rooms to send events for
  useEffect(() => {
    if (!socket || !socketReady) {
      return;
    }

    const previousSubscriptions = previousSubscriptionsRef.current;

    // Unsubscribe from rooms that are no longer needed
    // Compare previous subscriptions with current to find rooms to unsubscribe from
    previousSubscriptions.forEach((room) => {
      if (!subscriptions.includes(room)) {
        socket.emit('unsubscribe', room);
      }
    });

    // Subscribe to all current rooms that weren't previously subscribed
    // Only subscribe to new rooms to avoid duplicate subscription attempts
    subscriptions.forEach((room) => {
      if (!previousSubscriptions.includes(room)) {
        socket.emit('subscribe', room, (response: { success: boolean; message: string }) => {
          if (response.success) {
            console.log('subscribed to', room);
          } else {
            console.log('failed to subscribe to', room, response.message);
          }
        });
      }
    });

    // Update previous subscriptions for next render to track changes
    previousSubscriptionsRef.current = [...subscriptions];
  }, [socket, socketReady, subscriptions]);
}
